import React, { useState, useRef, useEffect, } from "react";
import { Helmet } from "react-helmet-async";
import { useForm } from "react-hook-form";
import { useMutation } from "graphql-hooks";
import { useNavigate } from "react-router-dom";
import { Link as RouterLink } from "react-router-dom";
import { Visibility, VisibilityOff } from "@mui/icons-material";
import { InputAdornment, IconButton } from "@mui/material";
import {
  Card,
  CardContent,
  Container,
  Divider,
  Link,
  Typography,
  Button,
  FormHelperText,
  TextField,
  Grid,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import Box from "@mui/material/Box";
import logoImg from "./assets/logo.png";
import loginImg from "./assets/login-image.png";
import mascotJoin from "./assets/login-mascot.png";
import { CREATE_USER_MUTATION } from "./graphQL/register_qry";

import useIsTablet from "./hooks/useIsTablet";
import useIsMobile from "./hooks/useIsMobile";

const StyledContainer = styled(Container)(() => ({
            
    minHeight: 624,
    angle: "0 deg",
    opacity: 1,
    borderRadius: "16px",
    boxShadow: "0px 4px 12px 0px #0000001F",
    display: "flex",
    justifyContent: "center",
    justifyItems: "center" ,

    // DESKTOP
    maxWidth: "1080px",
    margin: "48px 150px",

    "@media (max-width: 1360px)": {
        maxWidth: "802px",      // TABLET WIDTH
        margin: "32px auto",
    },

    "@media (max-width: 856px)": {
        maxWidth: "361px",      // MOBILE WIDTH
        margin: "8px auto",
    },
    
}));

const FormSide = styled(Box)(() => ({
  
   width: "540px",             // Desktop 2 columns = 1080 total
   padding: "32px",
   gap: "16px",

  "@media (max-width: 1360px)": 
  { width: "401px" },// Tablet 2 columns = 802 total
  
  "@media (max-width: 856px)":  
  {  width: "100%",            // Mobile = single column
    padding: "16px",          // Reduce padding on mobile },
    height: "auto",
    minHeight: "auto"
  },
  minHeight: 624,
  
  angle: "0 deg",
  opacity: 1,
}));

const StyledTextField = styled(TextField)(() => ({
    marginBottom: 5,
    marginTop: 30,
    "& .MuiInputLabel-outlined": {
      fontSize: 16,
    },
    "& .MuiOutlinedInput-root": {
      borderRadius: 10,
      fontSize: 16,
    },
}));

const StyledFormHelperTextError = styled(FormHelperText)(() => ({
    marginTop: 0,
    fontSize: 14, //16,
    fontFamily: "Arial",
}));

const StyledBox  = styled(Box)(() => ({
    color: "#212121",
    backgroundColor: "#F0FEF4",
    borderRadius: 12,
    padding: 16,
    fontSize: 16,
    fontFamily: "Arial",
    lineHeight: 1.4,
}));

const StyledButton = styled(Button)(() => ({
    width: "158px",
    height: "48px",
    backgroundColor: "#EF5658",
    textTransform: "none",
    "&:active": {
      backgroundColor: "#EF5658",
    },
    "&:hover": {
      backgroundColor: "#D33338",
    },
}));

const SignUp = () => {
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm();
  const form = useRef(null);
  const [successMsg, setSuccessMsg] = useState("");
  const [errorMsg, setErrorMsg] = useState("");
  const navigate = useNavigate();

  const isTablet = useIsTablet(1360); // Use 1360px breakpoint for tablet size
  const isMobile = useIsMobile(856); // Use 856px breakpoint for mobile size

  const [showPassword, setShowPassword] = useState(false);

  const handleTogglePassword = () => {
    setShowPassword((prev) => !prev);
  };

  const [showRepeatPassword, setShowRepeatPassword] = useState(false);

  const handleToggleRepeatPassword = () => {
    setShowRepeatPassword((prev) => !prev);
  };

  
  const [createUser] = useMutation(CREATE_USER_MUTATION, {
    awaitRefetchQueries: true,
  });

  /*
   * Function : setTimeout
   * Description : Disappear message after timeout
   */
  setTimeout(() => {
    setSuccessMsg("");
    setErrorMsg("");
  }, 10000000000);

    const onSubmit = async formData => {
    if (formData.password === formData.repeatPassword) {
      // await sleep(2000);
      console.log(formData);
      const { data, error } = await createUser({
        variables: {
          email: formData.email,
          password: formData.password,
          repeatPassword: formData.repeatPassword,
          username: formData.username,
        },
      });

      if (error) {
        console.log("Error", error, data);
        setErrorMsg(error.graphQLErrors[0].message);
        // setTimeout("");
      } else {
        console.log("Success");
        setSuccessMsg(
          "You must activate your account via email notification you will receive."
        );
        localStorage.setItem("userRegisterEmail", formData.email);
        localStorage.setItem("userRegisterUsername", formData.username);
        //
        navigate("/signup-success");
      }
    } else alert("Password and its repeat must be the same");
  };

  useEffect(() => {
    window.scrollTo(0, 0);    
  }, []);

  return (
    <Grid>
      <>
        <Helmet>
          <title>R3PLAY - Sign Up</title>
          <meta
            name="description"
            content="Sign up to buy, sell, and share toys with the R3PLAY community"
          />
          <meta
            property="keywords"
            content="Create account, join r3play, toy seller registration"
          />        
        </Helmet>
        <form ref={form} onSubmit={handleSubmit(onSubmit)}>            
          <Box
            sx={{
              position: "relative",
              display: "flex",
              alignItems: "center",
            }}
          >
            
          {!isMobile ?
            (
            <StyledContainer>
              <Box                  
                  display="grid"
                  gridTemplateColumns="1fr 1fr"                 
                  justifyContent="center"
                  justifyItems="center"       // center the fixed-width children
                  alignItems="start"
                  gap="0"
                  sx={{ width: "100%" }}
                >
                <FormSide sx={{ borderRight: "1px solid #e0e0e0" }}>
                    <Box
                      flexGrow={1}
                      alignItems="center"
                      display="flex"
                      justifyContent="space-between"
                    >
                      <Typography gutterBottom 
                        variant={!isTablet ? "h2" : "h3"}
                        fontSize={!isTablet ? "36px" : "30px"}
                        sx={{                        
                        fontFamily: "Roboto, Helvetica, Arial, sans-serif",
                        fontWeight: "600",
                        lineHeight: "1.75",
                        letterSpacing: "0.00938em"
                      }}>
                        Sign up
                      </Typography>
                    </Box>

                    <Box flexGrow={1} mb={1}>
                      <StyledTextField
                        fullWidth
                        //autoFocus
                        label="Email" // Address"
                        margin="normal"
                        name="email"
                        type="email"
                        variant="outlined"
                        id="email"
                        {...register("email", {
                          required: true,
                          pattern: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,4}$/i,
                        })}
                      />
                      {errors.email && errors.email.type === "required" && (
                        <StyledFormHelperTextError>
                          Email address cannot be blank.
                        </StyledFormHelperTextError>
                      )}
                      {errors.email && errors.email.type === "pattern" && (
                        <StyledFormHelperTextError>
                          Enter a correctly-formed email like myname@xyz.com
                        </StyledFormHelperTextError>
                      )}
                      <StyledTextField
                        fullWidth
                        label="Password"
                        margin="normal"
                        name="password"
                        type={showPassword ? "text" : "password"}
                        variant="outlined"
                        id="password"
                        {...register("password", { required: true })}

                        InputProps={{
                          endAdornment: (
                            <InputAdornment position="end">
                              <IconButton
                                onClick={handleTogglePassword}
                                edge="end"
                                aria-label="toggle password visibility"
                              >
                                {showPassword ? <VisibilityOff /> : <Visibility />}
                              </IconButton>
                            </InputAdornment>
                          )
                        }}
                      />
                      {errors.password && errors.password.type === "required" && (
                        <StyledFormHelperTextError>
                          Password cannot be blank.
                        </StyledFormHelperTextError>
                      )}
                      <StyledTextField
                        fullWidth
                        label="Repeat Password"
                        margin="normal"
                        name="repeatPassword"
                        type={showRepeatPassword ? "text" : "password"}
                        variant="outlined"
                        id="repeatPassword"
                        {...register("repeatPassword", { required: true })}

                        InputProps={{
                          endAdornment: (
                            <InputAdornment position="end">
                              <IconButton
                                onClick={handleToggleRepeatPassword}
                                edge="end"
                                aria-label="toggle password visibility"
                              >
                                {showRepeatPassword ? <VisibilityOff /> : <Visibility />}
                              </IconButton>
                            </InputAdornment>
                          )
                        }}
                      />
                      
                      {errors.repeatPassword && errors.repeatPassword.type === "required" && (
                        <StyledFormHelperTextError>
                          Repeat of password must be same as password.
                        </StyledFormHelperTextError>
                      )}
                      {!errors.repeatPassword && (
                        <Box ml={2}>
                          <Typography variant="body1"
                            sx={{
                            color: "rgba(33, 33, 33, 0.8)",
                            fontSize: "12px",
                            fontStyle: "normal",
                            fontFamily: "Roboto,Helvetica,Arial,sans-serif",
                            fontWeight: "400",
                            lineHeight: "1.7",
                            letterSpacing: "0.00938em",
                            }}>Enter your password again</Typography>
                        </Box>
                      )}
                      <StyledTextField
                        required
                        fullWidth
                        label="Username"
                        margin="normal"
                        name="username"
                        variant="outlined"
                        id="username"
                        {...register("username", {
                          required: true,
                          pattern: /^[a-zA-Z0-9]{6,12}$/i,
                        })}
                      />
                      {errors.username && errors.username.type === "required" && (
                        <Box ml={2}>
                          <StyledFormHelperTextError>
                            Username cannot be blank.
                          </StyledFormHelperTextError>
                        </Box>
                      )}
                      {errors.username && errors.username.type === "pattern" ? (
                        <Box ml={2}>
                          <StyledFormHelperTextError>
                            6 to 12 alpha-numeric characters.
                            <br /> No spaces, no special characters.
                          </StyledFormHelperTextError>
                        </Box>
                      ) : (
                        <Box ml={2} mr={2}>
                          <Typography variant="body1"
                            sx={{
                            color: "rgba(33, 33, 33, 0.8)",
                            fontSize: "12px",
                            fontStyle: "normal",
                            fontFamily: "Roboto,Helvetica,Arial,sans-serif",
                            fontWeight: "400",
                            lineHeight: "1.7",
                            letterSpacing: "0.00938em",
                            }}>
                            6 to 12 alpha-numeric characters.
                            <br /> No spaces, no special characters.
                          </Typography>
                        </Box>
                      )}
                      {errorMsg && (
                        <Box mt={1} mb={1} ml={2}>
                          <StyledFormHelperTextError>
                            {errorMsg}
                          </StyledFormHelperTextError>
                        </Box>
                      )}
                      {successMsg && (
                        <StyledBox mt={2}>
                          {successMsg}
                        </StyledBox>
                      )}
                      <Box mt={4} display="flex">
                        <StyledButton
                          color="primary"
                          type="submit"
                          variant="contained"
                        >
                          SUBMIT
                        </StyledButton>
                      </Box>
                    </Box>

                    <Box mt={2} display="flex" alignItems="center"
                         sx={{
                          fontSize: "16px",
                          fontStyle: "normal",
                          fontFamily: "Roboto,Helvetica,Arial,sans-serif",
                          fontWeight: "400",
                          lineHeight: "1.7",
                          letterSpacing: "0.00938em",
                        }}>
                      Already have an account?
                      <Link
                        ml={1}
                        component={RouterLink}
                        to="/signin"
                        color="primary"
                        underline="none"
                      >Sign in</Link>
                    </Box> 
                                     
                  </FormSide>
                  

                  <FormSide sx={{ paddingLeft: "0" }}>
                    <Box flexGrow={1}>
                     
                        <Box sx={{paddingLeft: "32px"}}>
                          <img src={logoImg} alt="logo" width={"40%"} />
                        </Box>
                      
                      <Typography variant="body1"
                        sx={{
                        color: "rgba(33, 33, 33, 0.8)",
                        fontSize: "1rem",
                        fontStyle: "normal",
                        fontFamily: "Roboto,Helvetica,Arial,sans-serif",
                        fontWeight: "400",
                        lineHeight: "1.7",
                        letterSpacing: "0.00938em",
                        paddingLeft: "32px"
                        }}>
                        <br />
                        Welcome to R3PLAY!
                        <br /><br />
                        Where nostalgia meets community and imagination comes back to life.
                        <br />
                        Join a space made for collectors, dreamers, and kidults at heart — where you can <b>buy, sell, trade, or donate</b> the toys that shaped your story. Here, every item has a memory, every connection sparks joy, and every action helps the planet through play.
                        <br />
                        Create your account, build your collection, and start reliving the magic — one R3PLAY at a time.
                      </Typography>
                      <Box>
                            <img src={loginImg} alt="logo" width={"100%"} />
                      </Box>
                      
                    </Box>                    
                  </FormSide>
                </Box>
              </StyledContainer>
              )
            :
            (
              <StyledContainer>
              <Box                  
                  display="grid"
                  gridTemplateColumns="1fr"                  
                  justifyContent="center"
                  justifyItems="center"       // center the fixed-width children                  
                  gap="1"
                  sx={{ width: "100%" }}
                >

                <FormSide >

                    <Box
                      flexGrow={1}
                      alignItems="center"
                      display="flex"
                      justifyContent="space-between"
                    >
                      <Typography gutterBottom 
                        variant="h3"
                        fontSize="30px"
                        sx={{                        
                        fontFamily: "Roboto, Helvetica, Arial, sans-serif",
                        fontWeight: "600",
                        lineHeight: "1.75",
                        letterSpacing: "0.00938em"
                      }}>
                        Sign up
                      </Typography>
                    </Box>
                    
                    <Box flexGrow={1}>
                     
                      <Typography variant="body1"
                        sx={{
                        color: "rgba(33, 33, 33, 0.8)",
                        fontSize: "1rem",
                        fontStyle: "normal",
                        fontFamily: "Roboto,Helvetica,Arial,sans-serif",
                        fontWeight: "400",
                        lineHeight: "1.7",
                        letterSpacing: "0.00938em",
                        
                        }}>
                        <br />
                        Welcome to R3PLAY!
                        <br /><br />
                        Where nostalgia meets community and imagination comes back to life.
                        <br />
                        Join a space made for collectors, dreamers, and kidults at heart — where you can <b>buy, sell, trade, or donate</b> the toys that shaped your story. Here, every item has a memory, every connection sparks joy, and every action helps the planet through play.
                        <br />
                        Create your account, build your collection, and start reliving the magic — one R3PLAY at a time.
                      </Typography>
                      <Box>
                            <img src={loginImg} alt="logo" width={"100%"} />
                      </Box>
                      
                    </Box>                    
                  </FormSide>

                <FormSide sx={{ borderRight: "none" }}>
                    

                    <Box flexGrow={1} mb={1}>
                      <StyledTextField
                        fullWidth
                        //autoFocus
                        label="Email" // Address"
                        margin="normal"
                        name="email"
                        type="email"
                        variant="outlined"
                        id="email"
                        {...register("email", {
                          required: true,
                          pattern: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,4}$/i,
                        })}
                      />
                      {errors.email && errors.email.type === "required" && (
                        <StyledFormHelperTextError>
                          Email address cannot be blank.
                        </StyledFormHelperTextError>
                      )}
                      {errors.email && errors.email.type === "pattern" && (
                        <StyledFormHelperTextError>
                          Enter a correctly-formed email like myname@xyz.com
                        </StyledFormHelperTextError>
                      )}
                      <StyledTextField
                        fullWidth
                        label="Password"
                        margin="normal"
                        name="password"
                        type={showPassword ? "text" : "password"}
                        variant="outlined"
                        id="password"
                        {...register("password", { required: true })}

                        InputProps={{
                          endAdornment: (
                            <InputAdornment position="end">
                              <IconButton
                                onClick={handleTogglePassword}
                                edge="end"
                                aria-label="toggle password visibility"
                              >
                                {showPassword ? <VisibilityOff /> : <Visibility />}
                              </IconButton>
                            </InputAdornment>
                          )
                        }}
                      />
                      {errors.password && errors.password.type === "required" && (
                        <StyledFormHelperTextError>
                          Password cannot be blank.
                        </StyledFormHelperTextError>
                      )}
                      <StyledTextField
                        fullWidth
                        label="Repeat Password"
                        margin="normal"
                        name="repeatPassword"
                        type={showRepeatPassword ? "text" : "password"}
                        variant="outlined"
                        id="repeatPassword"
                        {...register("repeatPassword", { required: true })}

                        InputProps={{
                          endAdornment: (
                            <InputAdornment position="end">
                              <IconButton
                                onClick={handleToggleRepeatPassword}
                                edge="end"
                                aria-label="toggle password visibility"
                              >
                                {showRepeatPassword ? <VisibilityOff /> : <Visibility />}
                              </IconButton>
                            </InputAdornment>
                          )
                        }}
                      />
                      {errors.repeatPassword && errors.repeatPassword.type === "required" && (
                        <StyledFormHelperTextError>
                          Repeat of password must be same as password.
                        </StyledFormHelperTextError>
                      )}
                      {!errors.repeatPassword && (
                        <Box ml={2}>
                          <Typography variant="body1"
                            sx={{
                            color: "rgba(33, 33, 33, 0.8)",
                            fontSize: "12px",
                            fontStyle: "normal",
                            fontFamily: "Roboto,Helvetica,Arial,sans-serif",
                            fontWeight: "400",
                            lineHeight: "1.7",
                            letterSpacing: "0.00938em",
                            }}>Enter your password again</Typography>
                        </Box>
                      )}
                      <StyledTextField
                        required
                        fullWidth
                        label="Username"
                        margin="normal"
                        name="username"
                        variant="outlined"
                        id="username"
                        {...register("username", {
                          required: true,
                          pattern: /^[a-zA-Z0-9]{6,12}$/i,
                        })}
                      />
                      {errors.username && errors.username.type === "required" && (
                        <Box ml={2}>
                          <StyledFormHelperTextError>
                            Username cannot be blank.
                          </StyledFormHelperTextError>
                        </Box>
                      )}
                      {errors.username && errors.username.type === "pattern" ? (
                        <Box ml={2}>
                          <StyledFormHelperTextError>
                            6 to 12 alpha-numeric characters.
                            <br /> No spaces, no special characters.
                          </StyledFormHelperTextError>
                        </Box>
                      ) : (
                        <Box ml={2} mr={2}>
                          <Typography variant="body1"
                            sx={{
                            color: "rgba(33, 33, 33, 0.8)",
                            fontSize: "12px",
                            fontStyle: "normal",
                            fontFamily: "Roboto,Helvetica,Arial,sans-serif",
                            fontWeight: "400",
                            lineHeight: "1.7",
                            letterSpacing: "0.00938em",
                            }}>
                            6 to 12 alpha-numeric characters.
                            <br /> No spaces, no special characters.
                          </Typography>
                        </Box>
                      )}
                      {errorMsg && (
                        <Box mt={1} mb={1} ml={2}>
                          <StyledFormHelperTextError>
                            {errorMsg}
                          </StyledFormHelperTextError>
                        </Box>
                      )}
                      {successMsg && (
                        <StyledBox mt={2}>
                          {successMsg}
                        </StyledBox>
                      )}
                      <Box mt={4} display="flex">
                        <StyledButton
                          color="primary"
                          type="submit"
                          variant="contained"
                        >
                          SUBMIT
                        </StyledButton>
                      </Box>
                    </Box>

                    <Box mt={2} display="flex" alignItems="center"
                         sx={{
                          fontSize: "16px",
                          fontStyle: "normal",
                          fontFamily: "Roboto,Helvetica,Arial,sans-serif",
                          fontWeight: "400",
                          lineHeight: "1.7",
                          letterSpacing: "0.00938em",
                        }}>
                      Already have an account?
                      <Link
                        ml={1}
                        component={RouterLink}
                        to="/signin"
                        color="primary"
                        underline="none"
                      >Sign in</Link>
                    </Box> 
                                     
                  </FormSide>                

                  
                </Box>
              </StyledContainer>
            )
          }
                {!isTablet && !isMobile
                  ?
                  (
                    <Box
                      sx={{
                        position: "relative",
                        bottom: "-130px",
                        right: 145,
                        cursor: "pointer",
                      }}
                    >
                      <Box
                        component="img"
                        src={mascotJoin}
                        alt="Join the Fun Mascot Orange"
                        sx={{
                          width: "140px",
                          height: "auto",
                          
                        }}
                      />
                    </Box>
                  )
                  :""
                }
          </Box>
          </form>
        </>
    </Grid>
  );
};

export default SignUp;
